// Step 2 of the Dhātu lab: the tenses of the chosen root as chips, grouped Beginner (वर्तमान, विधि-आज्ञा, भूत,
// भविष्यत्) and, for Adults, Advanced (कर्मवाच्य, प्रेरणार्थक, क्रियातिपत्ति). Only tables the engine has are offered.

import type { FC, ReactNode } from 'react'
import type { Tense } from '@/prakrit/lib/grammar/types'

import { Crown } from 'lucide-react'
import { TENSE_LABEL, TENSE_SHORT } from '@/prakrit/lib/grammar/labels'
import { ChipButton, StepLabel, pillKeyNav } from '@/prakrit/components/grammar/dhatu/dhatu-ui'
import { TENSE_FAMILIES, baseTense, familyOf } from '@/prakrit/components/grammar/dhatu/labels'

interface TenseChipsProps {
    basic: Tense[]
    advanced: Tense[]
    value: Tense
    onChange: (tense: Tense) => void
    /** Kids see the four beginner tenses only. */
    kids: boolean
    /** Tenses that need Premium for this visitor (shown with a crown). */
    locked?: (tense: Tense) => boolean
}

const TenseChip: FC<{ tense: Tense, label: Tense, active: boolean, locked?: boolean, onClick: () => void }> = ({ tense, label, active, locked = false, onClick }): ReactNode => (
    <ChipButton pressed={active} onClick={onClick} title={`${TENSE_LABEL[tense].hi} · ${TENSE_LABEL[tense].en}${locked ? ' · Premium' : ''}`}>
        <span lang='hi' className='hindi'>{TENSE_SHORT[label].hi}</span>
        <span lang='en' data-ex='en' className={active ? 'text-xs font-normal' : 'text-xs font-normal text-foreground/65'}>{TENSE_SHORT[label].en}</span>
        {locked && (
            <>
                <Crown className={active ? 'size-3.5' : 'size-3.5 text-saffron-strong'} aria-hidden='true' />
                <span className='sr-only'>Premium</span>
            </>
        )}
    </ChipButton>
)

export const TenseChips: FC<TenseChipsProps> = ({ basic, advanced, value, onChange, kids, locked }): ReactNode => {
    const families = TENSE_FAMILIES
        .map(f => ({ ...f, tenses: advanced.filter(t => familyOf(t) === f.value) }))
        .filter(f => f.tenses.length > 0)

    return (
        <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-1.5'>
                <StepLabel id='tense-basic' hi='आरम्भ' en='Beginner' />
                <div role='group' aria-labelledby='tense-basic' onKeyDown={pillKeyNav} className='flex flex-wrap gap-1.5'>
                    {basic.map(t => <TenseChip key={t} tense={t} label={t} active={t === value} locked={locked?.(t)} onClick={() => onChange(t)} />)}
                </div>
            </div>

            {!kids && families.length > 0 && (
                <div className='adults-only flex flex-col gap-2.5 border-t border-border pt-3'>
                    <StepLabel hi='आगे के रूप' en='Advanced' />
                    {families.map(f => (
                        <div key={f.value} className='flex flex-col gap-1.5'>
                            <p id={`tense-${f.value}`} className='text-xs text-muted-foreground'>
                                <span lang='hi' className='hindi font-medium'>{f.hi}</span>
                                <span aria-hidden='true'> · </span>
                                <span lang='en'>{f.en}</span>
                            </p>
                            <div role='group' aria-labelledby={`tense-${f.value}`} onKeyDown={pillKeyNav} className='flex flex-wrap gap-1.5'>
                                {f.tenses.map(t => (
                                    <TenseChip
                                        key={t}
                                        tense={t}
                                        label={f.value === 'kriyatipatti' ? t : baseTense(t)}
                                        active={t === value}
                                        locked={locked?.(t)}
                                        onClick={() => onChange(t)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {basic.length < 4 && (
                <p className='text-xs text-muted-foreground'>
                    <span lang='hi' data-ex='hi' className='hindi'>इस धातु की केवल ये तालिकाएँ पुस्तक में हैं।</span>
                    <span aria-hidden='true' className='ex-sep'> · </span>
                    <span lang='en' data-ex='en'>The book gives only these tables for this root.</span>
                </p>
            )}
        </div>
    )
}
